import { Fragment, ReactElement, useEffect, useState } from "react";
import { IAxisLinear, IBarChartConfig, ILineChartConfig, IPoint } from "./types";
import Chart from "./chart";
import { getMiddle, getTicks, useChartDimensions, useUniqueId } from "./chartUtils";
import AxisHorizontal from "./AxisHorizontal";
import AxisVertical from "./AxisVertical";
import * as d3 from "d3";
import Grid from "./grid";
import { formatNumber } from "../../system/utils";
import HtmlTooltip from "./tooltip";
import { useTheme } from "@mui/material";
import Legend from "./legend";
import { IDictionary } from "../../types";

interface IBarChartProps {
  barChartConfig: IBarChartConfig;
  showAccumulated?: boolean;
}

const barMargin = 2;
const paddingInner = 0.1;
const paddingOuter = 0.1;
const align = 0.5;

const BarChart = ({ barChartConfig, showAccumulated }: IBarChartProps): ReactElement => {
  const theme = useTheme();
  const [ accumChartConfig, setAccumChartConfig ] = useState<ILineChartConfig | null>(null);
  const [ maxLen, setMaxLen ] = useState(0);

  let headerItems = barChartConfig.title ? 1 : 0;
  headerItems += barChartConfig.showLegend ? 1 : 0;

  let marginRight = 10;
  barChartConfig.yAxis.forEach(x => {
    if (!x.hidden) {
      marginRight += 50;
    }
  });

  useEffect(() => {
    if (barChartConfig.accumulate) {
      let nameLen = 0;

      const accumSeries = barChartConfig.series.map(s => {
        let accum = 0;

        const value = (s.value as IPoint[]).map(p => {
          accum += p.y;

          return { x: p.x, y: accum };
        });

        nameLen = Math.max(s.name.length, nameLen);

        return {
          ...s,
          value,
          xAxisName: barChartConfig.xAxis.name,
          yAxisName: s.yAxisName
        };
      });

      const yAxis: IAxisLinear = {
        name: "AccumAxis",
        minimum: 0,
        maximum: 1, // roundUp(maximum, 0),
        ticksCount: 2
      };

      setAccumChartConfig({
        height: barChartConfig.height,
        xAxis: barChartConfig.xAxis,
        yAxis: [ yAxis ],
        series: accumSeries
      });

      setMaxLen(nameLen * 8 + 40);
    }
  }, [ barChartConfig ]);

  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: barChartConfig.height ?? undefined,
    width: barChartConfig.width ?? undefined,
    marginBottom: !barChartConfig.xAxis.hidden ? 35 : 10,
    marginLeft: 10,
    marginRight,
    marginTop: 10 + 20 * headerItems
  });

  const barID = useUniqueId("bar-chart");

  const xScale = d3.scaleBand()
    .domain(barChartConfig.xAxis.categories ?? barChartConfig.series.map(series => series.name))
    .range([0, dimensions.boundedWidth])
    .paddingInner(paddingInner)
    .paddingOuter(paddingOuter)
    .align(align);

  const maximumByAxis: Record<string, number> = {};
  const yScales: IDictionary<{ axis: d3.ScaleLinear<number, number, never>, config: IAxisLinear, }> = {};
  const accumChartScale = d3.scaleLinear()
    .domain([0.0, 100.0])
    .range([dimensions.boundedHeight, 0]);

  if (accumChartConfig) {
    accumChartConfig.series.forEach(s => {
      const values = (s.value as IPoint[]).map(p => p.y);

      if (!maximumByAxis[s.yAxisName]) {
        maximumByAxis[s.yAxisName] = 0;
      }

      maximumByAxis[s.yAxisName] = Math.max(maximumByAxis[s.yAxisName], ...values);
    });
  }

  barChartConfig.yAxis.forEach(axis => {
    yScales[axis.name] = {
      axis: d3.scaleLinear()
        .domain([axis.minimum, showAccumulated ? maximumByAxis[axis.name] : axis.maximum])
        .range([dimensions.boundedHeight, 0]),
      config: axis
    };

    if (!showAccumulated) {
      yScales[axis.name].axis.nice();
    }
  });


  const width = Math.max(!barChartConfig.multiple ? xScale.bandwidth() : xScale.bandwidth() / barChartConfig.series.length - barMargin, 0);
  const marginInner = paddingInner * width / 2.0;

  const categories: any = {};

  const xTicks = xScale.domain();

  const yGridTicks = getTicks(yScales[barChartConfig.yAxis[0].name].axis.domain() as number[], 3);

  let yIndex = -1;

  return (
    <div ref={ref} style={{ width: "100%", height: "calc(100% - 10px)" }} >
      <Chart dimensions={dimensions} >
        {/*<defs>*/}
        {/*  <filter id="shadow" x="0" y="0" width="200%" height="200%">*/}
        {/*    <feOffset result="offOut" in="SourceGraphic" dx="1" dy="1" />*/}
        {/*    <feGaussianBlur result="blurOut" in="offOut" stdDeviation="1" />*/}
        {/*    <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />*/}
        {/*  </filter>*/}
        {/*</defs>*/}

        <g>
          { !barChartConfig.xAxis.hidden &&
              <AxisHorizontal
                key={`${barID}-x-axis-${yIndex}`}
                chartID={barID}
                dimensions={dimensions}
                scale={xScale}
                label={barChartConfig.xAxis.name}
                fontSize={barChartConfig.xAxis.fontSize}
                ticks={xTicks}
                ticksAngle={barChartConfig.xAxis.ticksAngle}
                formatTick={barChartConfig.xAxis.formatTick}/>
          }

          { Object.keys(yScales).map((key) => {
            const yScale = yScales[key].axis;
            const yConfig = yScales[key].config;
            const yTicks = getTicks(yScale.domain(), yConfig.ticksCount);

            if(yConfig.hidden) {
              return;
            }

            yIndex++;

            return (
              <AxisVertical
                key={`${barID}-y-axis-${yIndex}`}
                chartID={barID}
                dimensions={dimensions}
                scale={yScale}
                label={yConfig.name}
                ticks={yTicks}
                formatTick={yConfig.formatTick}/>
            );
          })
          }
        </g>

        <text fontSize="1.5em" y={-15 * headerItems} fill={theme.palette.text.primary} className="Axis__label">{barChartConfig.title}</text>

        { barChartConfig.showLegend && <Legend chartID={barID} series={barChartConfig.series} width={dimensions.boundedWidth} /> }

        <Grid charID={barID} xScale={xScale} yScale={yScales[barChartConfig.yAxis[0].name].axis} dimensions={dimensions} yTicks={yGridTicks}/>

        <g>
          {barChartConfig.series.filter(x => !x.hidden).map((series, index) => {
            const yData = yScales[series.yAxisName];

            if (typeof series.value === "number") {
              const y = yData.axis(series.value as number) ?? 0;
              const x = xScale(series.name) ?? 0;
              const height = dimensions.boundedHeight - y;

              return (
                <Fragment key={`${barID}-${index}`}>
                  <HtmlTooltip title={
                    <div>
                      <div className="display-flex-row align-items-center" >
                        <div style={{ background: series.color, width: 10, height: 10, marginRight: 10 }} />
                        <strong className="flex-grow-1">{series.name}</strong>
                        <strong className="flex-grow-0">
                          {formatNumber(series.value as number, 1)}
                          {series.unit && <sub>{`[${series.unit}]`}</sub>}
                        </strong>
                      </div>
                    </div>
                  }>
                    <g>
                      <rect
                        className="Bars__rect"
                        fill={series.color}
                        height={height}
                        width={width}
                        y={y}
                        x={x + marginInner}
                      />
                      {barChartConfig.showValue &&
                        <text x={x + marginInner + getMiddle(width)} y={y} textAnchor="middle"
                          alignmentBaseline="middle"
                          fontWeight="bold" fill="white">
                          {yData.config.formatTick &&yData.config.formatTick(series.value as number, 0, 0)}
                        </text>}
                    </g>
                  </HtmlTooltip>
                </Fragment>);
            }

            return (
              (series.value as number[] | IPoint[]).map((value, valueIndex) => {
                const categoryName = typeof value === "number" ? series.name : value.x as string;
                const y = yData.axis(typeof value === "number" ? value : value.y) ?? 0;
                const x = xScale(categoryName);
                if (x === undefined) {
                  return null;
                }

                const height = Math.max(0, dimensions.boundedHeight - y);

                if (categories[categoryName] === undefined) {
                  categories[categoryName] = height;
                } else {
                  categories[categoryName] += height;
                }

                const barX = x + (width + barMargin) * index + barMargin + marginInner;
                const barY = y;

                let textColor = "#8395a7";
                let textY = barY - 5;
                if (textY < 5) {
                  textY += 12;
                  textColor = "white";
                }

                return (
                  <Fragment key={`${barID}-${index}-${valueIndex}`}>
                    <HtmlTooltip title={
                      <div className="display-flex-column">
                        <div className="display-flex-column align-items-center" >
                          <strong>{categoryName}</strong>
                        </div>
                        <div className="display-flex-row align-items-center" >
                          <div style={{ background: series.color, width: 10, height: 10, marginRight: 10 }} />
                          <strong className="flex-grow-1">{series.name}</strong>
                          <strong className="flex-grow-0">
                            {formatNumber(typeof value === "number" ? value : value.y, 1)}
                            {series.unit && <sub>{`[${series.unit}]`}</sub>}
                          </strong>
                        </div>
                      </div>
                    }>
                      <g>
                        <rect
                          className="Bars__rect"
                          fill={series.color}
                          height={height}
                          width={width}
                          y={barY}
                          x={barX}
                        />
                        {barChartConfig.showValue && (typeof value === "number" ? value : value.y) > 0 &&
                          <text x={barX + getMiddle(width)} y={textY} textAnchor="middle"
                            alignmentBaseline="middle"
                            fill={textColor}
                            fontSize="0.8em">
                            {yData.config.formatTick && yData.config.formatTick(typeof value === "number" ? value : value.y, 0, 0)}
                          </text>}
                      </g>
                    </HtmlTooltip>
                  </Fragment>
                );
              }));
          })
          }
        </g>

        {showAccumulated && accumChartConfig &&
          <g>
            <g>
              <rect width={dimensions.boundedWidth} height={dimensions.boundedHeight} fill="black" fillOpacity={0.2}/>
              {accumChartConfig.series.map((series, index) => {
                const points = series.value as IPoint[];

                return (
                  <g key={`accum-series-legend-${index}`}>
                    <circle r={5} fill={series.color} cy={20 + index * 15} cx={10} stroke={series.color} strokeWidth={0} />
                    <text y={20 + index * 15} x={25} fill={theme.palette.text.primary} fontSize={"0.9em"}>{series.name}</text>

                    <text y={20 + index * 15} x={maxLen + 20} fill={theme.palette.text.primary} fontSize={"0.9em"} textAnchor={"end"}>
                      { formatNumber(points.at(-1)?.y ?? 0, 2) }
                    </text>
                  </g>
                );
              })
              }
            </g>

            {accumChartConfig.series.map((series, index) => {
              const key = `accum-series-${index}`;
              const points = series.value as IPoint[];

              if (points.length === 0) {
                return null;
              }

              const max = points[points.length - 1].y;
              const axisMax = yScales[series.yAxisName].axis.domain()[1];

              const fy = (d: IPoint, i: number) => i === 0
                ? accumChartScale(100 * d.y / axisMax)
                : accumChartScale(100 * d.y / max);

              const barX = (width + barMargin) * index + barMargin + marginInner + width / 2.0;

              const lineGenerator = d3.line<IPoint>()
                .x((d: IPoint) => (xScale(d.x as any) ?? 0) + barX)
                .y(fy)
                .curve(d3.curveLinear);

              return (
                <g key={`accum-series-${index}`} >
                  <path
                    key={key}
                    className={"Line"}
                    fill="none"
                    stroke={series.color}
                    strokeWidth={2}
                    d={lineGenerator(series.value as any) ?? ""}
                  />

                  { points.map((point, pointIndex) => {

                    return (
                      <HtmlTooltip
                        key={`${key}-point-${pointIndex}`}
                        title={
                          <div className="display-flex-column">
                            <div className="display-flex-row align-items-center" >
                              <div style={{ background: series.color, width: 10, height: 10, marginRight: 10 }} />
                              <strong className="flex-grow-1 mr-2">{series.name}</strong>
                              <strong className="flex-grow-0">
                                { formatNumber(point.y, 2) }
                                {series.unit && <sub>{`[${series.unit}]`}</sub>}
                              </strong>
                            </div>
                          </div>
                        }>
                        <circle cx={(xScale(point.x as any) ?? 0) + barX} cy={fy(point, pointIndex)} r="2" fill="white" stroke={series.color} strokeWidth={1}/>
                      </HtmlTooltip>
                    );
                  })}
                </g>
              );
            })}
          </g>
        }

      </Chart>
    </div>);
};

export default BarChart;
